import { Code2, FileText, FolderGit2, ListChecks, Mic } from 'lucide-react';

import type { WorkIntegrity, WorkKind, WorkStatus } from '@/lib/api/trainer';

/**
 * The words and colours the two work views share.
 *
 * A student's feed and a work item's roster are the same evidence pivoted, so
 * they must say the same things about it. Two copies of these tables would
 * drift, and the day they disagree is the day a trainer sees one screen call a
 * quiz "done" and another call it "awaiting grading".
 */

export const KIND_ICON: Record<WorkKind, typeof Code2> = {
  practice: Code2,
  assignment: FileText,
  quiz: ListChecks,
  project: FolderGit2,
  viva: Mic,
};

export const KIND_LABEL: Record<WorkKind, string> = {
  practice: 'Practice',
  assignment: 'Assignment',
  quiz: 'Quiz',
  project: 'Project',
  viva: 'Viva',
};

export const STATUS_LABEL: Record<WorkStatus, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  submitted: 'Awaiting grading',
  done: 'Done',
};

export const STATUS_TONE: Record<WorkStatus, string> = {
  not_started: 'border-muted bg-muted text-muted-foreground',
  in_progress: 'border-amber-200 bg-amber-50 text-amber-700',
  submitted: 'border-sky-200 bg-sky-50 text-sky-700',
  done: 'border-green-200 bg-green-100 text-green-700',
};

/**
 * Wording is descriptive, never accusatory. "Arrived whole" says what happened;
 * anything stronger would claim to know why, which this cannot. There is no red
 * anywhere in here on purpose — a colour that reads as an accusation makes the
 * trainer's opening question an accusation too.
 */
export const SIGNAL_LABEL: Record<WorkIntegrity['signal'], string> = {
  // Practice: what the editor recorded.
  self_solved: 'Written here',
  assisted: 'Part pasted',
  pasted: 'Arrived whole',
  // Assignments and projects: what the repository's commit history shows.
  // Nothing can be watched being typed when the work happens on GitHub, so the
  // question is answered from the other end — how the repository was built.
  built_over_time: 'Built over time',
  one_sitting: 'Done in one sitting',
  one_commit: 'Arrived in one commit',
  insufficient: '',
};

export const SIGNAL_TONE: Record<WorkIntegrity['signal'], string> = {
  self_solved: 'text-green-700',
  assisted: 'text-sky-700',
  pasted: 'text-sky-800',
  built_over_time: 'text-green-700',
  // Neutral grey, not amber: doing a small assignment in one evening is what
  // most students do, and colouring the common case trains people to ignore it.
  one_sitting: 'text-muted-foreground',
  one_commit: 'text-sky-800',
  insufficient: '',
};

/**
 * Whether a signal is worth showing at all.
 *
 * `insufficient` means the sample was too small to say anything — a two-line
 * answer typed in eight seconds. Rendering it as a label would dress up "we
 * don't know" as a finding.
 */
export function hasSignal(integrity?: WorkIntegrity): boolean {
  return !!integrity && integrity.signal !== 'insufficient';
}

/** Dates only. The exact minute is never what a trainer is asking. */
export function formatWhen(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { day: '2-digit', month: 'short' });
}
